/**
 * Shaded relief of a DTM raster for the planner map: the viridis height ramp lit by a hillshade
 * (light from the north-west, 45° up). Pure pixel maths; the canvas step is in `shadeImage.ts`.
 */
import { viridis } from '@/theme/colors';
import { fromMercator, type Bbox, type DtmRaster } from './cuzk';

export type { DtmRaster } from './cuzk';

export interface Shaded {
  pixels: Uint8ClampedArray;
  width: number;
  height: number;
  bounds: Bbox;
  min: number | null;
  max: number | null;
}

const AZIMUTH_MATH = (135 * Math.PI) / 180; // light from the north-west (azimuth 315°), in the maths convention
const ZENITH = Math.PI / 4; // 45° above the horizon
const AMBIENT = 0.3;

function valid(v: number): boolean {
  return Number.isFinite(v) && v > -1000 && v < 9000;
}

/** Horn's hillshade, 0..1 per pixel; edges reuse their nearest neighbour and no-data cells their centre. */
export function hillshade(raster: DtmRaster): Float32Array {
  const { data, width, height, mercator } = raster;
  // Mercator metres shrink by cos(lat) on the ground.
  const midLat = fromMercator(0, (mercator.ymin + mercator.ymax) / 2)[1];
  const scale = Math.cos((midLat * Math.PI) / 180);
  const px = ((mercator.xmax - mercator.xmin) / width) * scale;
  const py = ((mercator.ymax - mercator.ymin) / height) * scale;
  const out = new Float32Array(width * height);
  const at = (x: number, y: number, centre: number) => {
    const v = data[Math.min(height - 1, Math.max(0, y)) * width + Math.min(width - 1, Math.max(0, x))];
    return valid(v) ? v : centre;
  };
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const c = data[y * width + x];
      if (!valid(c)) continue;
      const a = at(x - 1, y - 1, c), b = at(x, y - 1, c), cc = at(x + 1, y - 1, c);
      const d = at(x - 1, y, c), f = at(x + 1, y, c);
      const g = at(x - 1, y + 1, c), h = at(x, y + 1, c), i = at(x + 1, y + 1, c);
      const dzdx = (cc + 2 * f + i - (a + 2 * d + g)) / (8 * px);
      const dzdy = (g + 2 * h + i - (a + 2 * b + cc)) / (8 * py);
      const slope = Math.atan(Math.hypot(dzdx, dzdy));
      const aspect = Math.atan2(dzdy, -dzdx);
      out[y * width + x] = Math.max(0, Math.cos(ZENITH) * Math.cos(slope) + Math.sin(ZENITH) * Math.sin(slope) * Math.cos(AZIMUTH_MATH - aspect));
    }
  }
  return out;
}

function rampTable(): Uint8ClampedArray {
  const lut = new Uint8ClampedArray(256 * 3);
  for (let i = 0; i < 256; i++) {
    const m = /rgb\((\d+),(\d+),(\d+)\)/.exec(viridis(i / 255));
    lut[i * 3] = Number(m?.[1]);
    lut[i * 3 + 1] = Number(m?.[2]);
    lut[i * 3 + 2] = Number(m?.[3]);
  }
  return lut;
}

export function shadeDtm(raster: DtmRaster): Shaded {
  const { data, width, height, mercator } = raster;
  let min = Infinity;
  let max = -Infinity;
  for (const v of data) {
    if (!valid(v)) continue;
    if (v < min) min = v;
    if (v > max) max = v;
  }
  const pixels = new Uint8ClampedArray(width * height * 4);
  if (min !== Infinity) {
    const shade = hillshade(raster);
    const lut = rampTable();
    const span = max - min || 1;
    for (let k = 0; k < width * height; k++) {
      const v = data[k];
      if (!valid(v)) continue;
      const t = Math.round(((v - min) / span) * 255) * 3;
      const light = AMBIENT + (1 - AMBIENT) * shade[k];
      pixels[k * 4] = lut[t] * light;
      pixels[k * 4 + 1] = lut[t + 1] * light;
      pixels[k * 4 + 2] = lut[t + 2] * light;
      pixels[k * 4 + 3] = 255;
    }
  }
  const sw = fromMercator(mercator.xmin, mercator.ymin);
  const ne = fromMercator(mercator.xmax, mercator.ymax);
  return {
    pixels,
    width,
    height,
    bounds: { west: sw[0], south: sw[1], east: ne[0], north: ne[1] },
    min: min === Infinity ? null : min,
    max: max === -Infinity ? null : max,
  };
}
